"use client";

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

/**
 * JSON shape we persist per table tennis match
 * {
 *   matchType: "best_of_5" | "best_of_7",
 *   gameMode: "singles" | "doubles",
 *   games: [{ userPoints, opponentPoints }],
 *   userGames: number,
 *   opponentGames: number,
 *   winner: "user" | "opponent" | null,
 *   playerA: string | null, playerAId: string | null,
 *   playerB: string | null, playerBId: string | null,
 *   teamA: { player1: string | null, player1Id: string | null, player2: string | null, player2Id: string | null },
 *   teamB: { player1: string | null, player1Id: string | null, player2: string | null, player2Id: string | null }
 * }
 */

export const AddTableTennis = () => {
  const location = useLocation();
  const data = location.state || {};

  const [tableTennisMatches, setTableTennisMatches] = useState([]);
  const navigate = useNavigate();
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  // Tailwind class helpers for consistent styling
  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
    matchCard: {
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "25px",
      marginBottom: "25px",
      backgroundColor: "#f8fafc",
      boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
    },
    matchHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #e2e8f0",
      paddingBottom: "15px",
      marginBottom: "20px",
    },
    matchTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#2c3e50",
      margin: "0",
    },
    removeButton: {
      backgroundColor: "#e53e3e",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    formGroup: {
      marginBottom: "20px",
    },
    formRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: "15px",
      gap: "10px",
    },
    label: {
      fontWeight: "500",
      color: "#4a5568",
      minWidth: "100px",
    },
    select: {
      padding: "10px",
      borderRadius: "6px",
      border: "1px solid #cbd5e0",
      backgroundColor: "white",
      fontSize: "16px",
      outline: "none",
      minWidth: "200px",
    },
    addGameButton: {
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      padding: "10px 16px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "10px",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      marginTop: "20px",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    },
    tableHeader: {
      backgroundColor: "#edf2f7",
      color: "#2d3748",
      fontWeight: "600",
      padding: "12px 15px",
      textAlign: "left",
      borderBottom: "2px solid #cbd5e0",
    },
    tableCell: {
      padding: "12px 15px",
      borderBottom: "1px solid #e2e8f0",
      backgroundColor: "white",
    },
    scoreCard: {
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: "white",
      padding: "20px",
      borderRadius: "8px",
      marginTop: "20px",
      boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
    },
    scoreBox: {
      textAlign: "center",
      padding: "15px",
      borderRadius: "8px",
      minWidth: "120px",
    },
    scoreLabel: {
      fontSize: "14px",
      color: "#718096",
      marginBottom: "5px",
    },
    scoreValue: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#2d3748",
    },
    winnerBox: {
      textAlign: "center",
      padding: "15px",
      borderRadius: "8px",
      backgroundColor: "#ebf8ff",
      minWidth: "120px",
    },
    winnerLabel: {
      fontSize: "14px",
      color: "#2b6cb0",
      marginBottom: "5px",
    },
    winnerValue: {
      fontSize: "20px",
      fontWeight: "700",
      color: "#2c5282",
    },
    removeGameButton: {
      backgroundColor: "#f56565",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    numberInput: {
      width: "60px",
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #cbd5e0",
      textAlign: "center",
      fontSize: "16px",
    },
    teamSection: {
      marginBottom: "20px",
    },
    guidanceBox: {
      backgroundColor: "#f7fafc",
      padding: "20px",
      borderRadius: "8px",
      marginBottom: "20px",
    },
    guidanceTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#2d3748",
      marginBottom: "10px",
    },
    guidanceContent: {
      fontSize: "16px",
      color: "#718096",
    },
  };

  const handleSaveAll = async () => {
    // Validate matches before saving
    const validationErrors = validateAllMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following issues before saving:\n\n" +
          validationErrors.join("\n")
      );
      return;
    }

    // Validate matches before saving
    const incompleteMatches = tableTennisMatches.filter(
      (match) => !isMatchComplete(match)
    );

    if (incompleteMatches.length > 0) {
      toast.error(
        "Please complete all matches before saving. Each match must have a winner according to the selected match type."
      );
      return;
    }

    console.log("Saving all table tennis matches...");

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: tableTennisMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all table tennis matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  // Validation function for all matches
  const validateAllMatches = () => {
    const errors = [];

    tableTennisMatches.forEach((match, index) => {
      const matchErrors = validateTableTennisMatch(match, index + 1);
      errors.push(...matchErrors);
    });

    return errors;
  };

  // Validate individual table tennis match
  const validateTableTennisMatch = (match, matchNumber) => {
    const errors = [];

    // Check if players/teams are selected
    if (match.gameMode === "singles") {
      if (!match.playerA || !match.playerB) {
        errors.push(
          `Match ${matchNumber}: Both players must be selected for singles`
        );
      }
      if (match.playerA && match.playerB && match.playerA === match.playerB) {
        errors.push(
          `Match ${matchNumber}: Cannot select the same player for both positions`
        );
      }
    } else {
      if (
        !match.teamA?.player1 ||
        !match.teamA?.player2 ||
        !match.teamB?.player1 ||
        !match.teamB?.player2
      ) {
        errors.push(
          `Match ${matchNumber}: All team players must be selected for doubles`
        );
      }
    }

    // Check if match has games
    if (!match.games || match.games.length === 0) {
      errors.push(`Match ${matchNumber}: Match must have at least one game`);
    }

    // Validate game scores
    if (match.games && match.games.length > 0) {
      match.games.forEach((game, gameIndex) => {
        // Check for valid point scores (0-11, with proper table tennis scoring rules)
        // In deuce situations (both players >= 10), allow scores above 11 up to 20
        const isDeuce = game.userPoints >= 10 && game.opponentPoints >= 10;
        const maxAllowedPoints = isDeuce ? 20 : 11;

        if (
          game.userPoints > maxAllowedPoints ||
          game.opponentPoints > maxAllowedPoints
        ) {
          errors.push(
            `Match ${matchNumber}, Game ${
              gameIndex + 1
            }: Points cannot exceed ${maxAllowedPoints}${
              isDeuce ? " (deuce situation)" : ""
            }`
          );
        }

        // Check for valid game completion
        if (!isDeuce && game.userPoints < 11 && game.opponentPoints < 11) {
          errors.push(
            `Match ${matchNumber}, Game ${
              gameIndex + 1
            }: At least one player must reach 11 points`
          );
        }

        // Check for proper winning conditions (must win by 2 points if score is 10-10 or higher)
        if (game.userPoints >= 10 && game.opponentPoints >= 10) {
          const diff = Math.abs(game.userPoints - game.opponentPoints);
          if (diff < 2) {
            errors.push(
              `Match ${matchNumber}, Game ${
                gameIndex + 1
              }: Must win by 2 points when score reaches 10-10`
            );
          }
        }
      });
    }

    // Check match completion based on match type
    const requiredWins = match.matchType === "best_of_5" ? 3 : 4;
    if (match.userGames < requiredWins && match.opponentGames < requiredWins) {
      errors.push(
        `Match ${matchNumber}: Match incomplete - need ${requiredWins} game wins for ${match.matchType}`
      );
    }

    return errors;
  };

  const isMatchComplete = (match) => {
    // Determine if match is complete based on match type
    const requiredWins = match.matchType === "best_of_5" ? 3 : 4;
    return (
      match.userGames === requiredWins || match.opponentGames === requiredWins
    );
  };

  const handleAddTableTennisMatch = () => {
    const newMatch = {
      matchType: "best_of_5", // Default to best of 5
      gameMode: "singles", // singles or doubles
      games: [],
      userGames: 0,
      opponentGames: 0,
      winner: null,
      // For singles - store both player names and IDs
      playerA: null,
      playerAId: null,
      playerB: null,
      playerBId: null,
      // For doubles - store both player names and IDs
      teamA: { player1: null, player1Id: null, player2: null, player2Id: null },
      teamB: { player1: null, player1Id: null, player2: null, player2Id: null },
    };
    setTableTennisMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setTableTennisMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setTableTennisMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />
      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Match at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed table tennis match information"
            )}
          </p>
        </div>

        <button
          className={classes.addButton}
          onClick={handleAddTableTennisMatch}
        >
          Add New Table Tennis Match
        </button>

        {tableTennisMatches.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No matches added yet. Click the button above to add your first table
            tennis match.
          </div>
        )}

        {tableTennisMatches.map((match, index) => (
          <TableTennisMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {tableTennisMatches.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const TableTennisMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    formGroup: "space-y-4 mb-5",
    label: "font-medium text-gray-700 block mb-2",
    select:
      "w-full max-w-[240px] px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white",
    numberInput: "w-20 px-3 py-2 border border-gray-300 rounded text-center",
    scoreCard:
      "flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3",
    scoreBox:
      "text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-3xl font-bold text-gray-800",
    winnerBox:
      "text-center p-4 rounded-lg bg-blue-50 min-w-[120px] w-full md:w-auto",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
    playerSection: "bg-white p-4 rounded-lg shadow-sm mb-4",
    playerRow: "grid grid-cols-1 md:grid-cols-2 gap-4",
  };

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const handleMatchTypeChange = (e) => {
    const updated = {
      ...localMatch,
      matchType: e.target.value,
      games: [],
      userGames: 0,
      opponentGames: 0,
      winner: null,
    };
    updateParent(updated);
  };

  const handleGameModeChange = (e) => {
    const updated = {
      ...localMatch,
      gameMode: e.target.value,
      games: [],
      userGames: 0,
      opponentGames: 0,
      winner: null,
    };
    updateParent(updated);
  };

  const handleAddGame = () => {
    // Check if match is already complete
    const requiredWins = localMatch.matchType === "best_of_5" ? 3 : 4;
    if (
      localMatch.userGames === requiredWins ||
      localMatch.opponentGames === requiredWins
    ) {
      alert(
        `Match is already complete. A ${localMatch.matchType} match requires ${requiredWins} game wins.`
      );
      return;
    }

    const newGame = {
      userPoints: 0,
      opponentPoints: 0,
    };
    const updated = {
      ...localMatch,
      games: [...localMatch.games, newGame],
    };
    recalculateMatchResults(updated);
  };

  const handleGameChange = (index, field, value) => {
    // Validate and limit points
    let limitedValue = Math.max(0, Number(value));

    // Allow points above 11 only in deuce situations
    const currentGame = localMatch.games[index];
    const otherField = field === "userPoints" ? "opponentPoints" : "userPoints";
    const otherValue = currentGame[otherField];

    // If both players have 10+ points, allow higher scores for deuce
    if (limitedValue >= 10 && otherValue >= 10) {
      limitedValue = Math.min(limitedValue, 20); // Reasonable upper limit for deuce
    } else {
      limitedValue = Math.min(limitedValue, 11); // Normal limit
    }

    const updatedGames = localMatch.games.map((game, i) => {
      if (i === index) {
        return { ...game, [field]: limitedValue };
      }
      return game;
    });
    const updated = { ...localMatch, games: updatedGames };
    recalculateMatchResults(updated);
  };

  const handleRemoveGame = (index) => {
    const updatedGames = localMatch.games.filter((_, i) => i !== index);
    const updated = { ...localMatch, games: updatedGames };
    recalculateMatchResults(updated);
  };

  const recalculateMatchResults = (updatedMatch) => {
    let userGames = 0;
    let opponentGames = 0;

    updatedMatch.games.forEach((game) => {
      // Determine game winner according to table tennis rules
      const isDeuce = game.userPoints >= 10 && game.opponentPoints >= 10;
      const pointDifference = Math.abs(game.userPoints - game.opponentPoints);

      if (isDeuce) {
        // In deuce situations, player must win by 2 points
        if (pointDifference >= 2 && game.userPoints > game.opponentPoints) {
          userGames++;
        } else if (
          pointDifference >= 2 &&
          game.opponentPoints > game.userPoints
        ) {
          opponentGames++;
        }
      } else {
        // Normal table tennis rules (first to 11, win by 2)
        if (
          (game.userPoints >= 11 &&
            game.userPoints > game.opponentPoints + 1) ||
          (game.userPoints > game.opponentPoints && game.userPoints >= 11)
        ) {
          userGames++;
        } else if (
          (game.opponentPoints >= 11 &&
            game.opponentPoints > game.userPoints + 1) ||
          (game.opponentPoints > game.userPoints && game.opponentPoints >= 11)
        ) {
          opponentGames++;
        }
      }
    });

    updatedMatch.userGames = userGames;
    updatedMatch.opponentGames = opponentGames;

    // Determine match winner based on match type (best of 5 or best of 7)
    const requiredWins = updatedMatch.matchType === "best_of_5" ? 3 : 4;

    if (userGames === requiredWins) {
      updatedMatch.winner = "user";
    } else if (opponentGames === requiredWins) {
      updatedMatch.winner = "opponent";
    } else {
      updatedMatch.winner = null;
    }

    updateParent(updatedMatch);
  };

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Table Tennis Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      {/* Table Tennis Scoring Guidance */}
      <div className={formClasses.guidanceBox}>
        <h4 className={formClasses.guidanceTitle}>
          🏓 Table Tennis Scoring Guide
        </h4>
        <div className={formClasses.guidanceContent}>
          <p>
            <strong>Match Type:</strong>{" "}
            {localMatch.matchType === "best_of_5"
              ? "Best of 5 Games (first to win 3)"
              : "Best of 7 Games (first to win 4)"}
          </p>
          <p>
            <strong>Game Mode:</strong>{" "}
            {localMatch.gameMode === "singles"
              ? "Singles (1 vs 1)"
              : "Doubles (2 vs 2)"}
          </p>
          <p>
            <strong>Game Scoring:</strong> First to 11 points wins the game
            (must win by 2 points)
          </p>
          <p>
            <strong>Deuce Rule:</strong> If score reaches 10-10, continue until
            one player leads by 2 points (scores can go above 11)
          </p>
          <p>
            <strong>Points Range:</strong> 0-11 points per game (can go up to 20
            in deuce situations when both players reach 10+ points)
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <label className={formClasses.label}>Match Type:</label>
        <select
          value={localMatch.matchType}
          onChange={handleMatchTypeChange}
          className={formClasses.select}
        >
          <option value="best_of_5">Best of 5</option>
          <option value="best_of_7">Best of 7</option>
        </select>
      </div>

      <div className={formClasses.formGroup}>
        <label className={formClasses.label}>Game Mode:</label>
        <select
          value={localMatch.gameMode}
          onChange={handleGameModeChange}
          className={formClasses.select}
        >
          <option value="singles">Singles</option>
          <option value="doubles">Doubles</option>
        </select>
      </div>

      {/* Player/Team Selection based on game mode */}
      {localMatch.gameMode === "singles" ? (
        <div className={formClasses.playerSection}>
          <div className={formClasses.playerRow}>
            <div>
              <label className={formClasses.label}>Player A:</label>
              <PlayerSlot
                availableUsers={availableUsers}
                selectedPlayerId={localMatch.playerAId}
                selectedPlayerName={
                  localMatch.playerAId === "guest"
                    ? localMatch.playerA || "Player A"
                    : localMatch.playerA
                    ? availableUsers.find(
                        (u) => (u.id || u.user_id) == localMatch.playerA
                      )?.full_name || "Player A"
                    : "Player A"
                }
                selectedUserIds={[
                  localMatch.playerAId !== "guest" && localMatch.playerAId
                    ? localMatch.playerAId
                    : null,
                  localMatch.playerBId !== "guest" && localMatch.playerBId
                    ? localMatch.playerBId
                    : null,
                ].filter(Boolean)}
                onSelectPlayer={(userInfo, customName) => {
                  if (customName) {
                    const updated = {
                      ...localMatch,
                      playerA: customName,
                      playerAId: "guest",
                    };
                    updateParent(updated);
                  } else if (userInfo) {
                    const updated = {
                      ...localMatch,
                      playerA: userInfo.id || userInfo.user_id,
                      playerAId: userInfo.id || userInfo.user_id,
                    };
                    updateParent(updated);
                  }
                }}
              />
            </div>
            <div>
              <label className={formClasses.label}>Player B:</label>
              <PlayerSlot
                availableUsers={availableUsers}
                selectedPlayerId={localMatch.playerBId}
                selectedPlayerName={
                  localMatch.playerBId === "guest"
                    ? localMatch.playerB || "Player B"
                    : localMatch.playerB
                    ? availableUsers.find(
                        (u) => (u.id || u.user_id) == localMatch.playerB
                      )?.full_name || "Player B"
                    : "Player B"
                }
                selectedUserIds={[
                  localMatch.playerAId !== "guest" && localMatch.playerAId
                    ? localMatch.playerAId
                    : null,
                  localMatch.playerBId !== "guest" && localMatch.playerBId
                    ? localMatch.playerBId
                    : null,
                ].filter(Boolean)}
                onSelectPlayer={(userInfo, customName) => {
                  if (customName) {
                    const updated = {
                      ...localMatch,
                      playerB: customName,
                      playerBId: "guest",
                    };
                    updateParent(updated);
                  } else if (userInfo) {
                    const updated = {
                      ...localMatch,
                      playerB: userInfo.id || userInfo.user_id,
                      playerBId: userInfo.id || userInfo.user_id,
                    };
                    updateParent(updated);
                  }
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Team A Selection */}
          <div className={formClasses.playerSection}>
            <h4 className="text-lg font-semibold text-gray-700 mb-3">Team A</h4>
            <div className={formClasses.playerRow}>
              <div>
                <label className={formClasses.label}>Player 1:</label>
                <PlayerSlot
                  availableUsers={availableUsers}
                  selectedPlayerId={localMatch.teamA?.player1Id}
                  selectedPlayerName={
                    localMatch.teamA?.player1Id === "guest"
                      ? localMatch.teamA?.player1 || "Player 1"
                      : localMatch.teamA?.player1
                      ? availableUsers.find(
                          (u) => (u.id || u.user_id) == localMatch.teamA.player1
                        )?.full_name || "Player 1"
                      : "Player 1"
                  }
                  selectedUserIds={[
                    localMatch.teamA?.player1Id !== "guest" &&
                    localMatch.teamA?.player1Id
                      ? localMatch.teamA.player1Id
                      : null,
                    localMatch.teamA?.player2Id !== "guest" &&
                    localMatch.teamA?.player2Id
                      ? localMatch.teamA.player2Id
                      : null,
                    localMatch.teamB?.player1Id !== "guest" &&
                    localMatch.teamB?.player1Id
                      ? localMatch.teamB.player1Id
                      : null,
                    localMatch.teamB?.player2Id !== "guest" &&
                    localMatch.teamB?.player2Id
                      ? localMatch.teamB.player2Id
                      : null,
                  ].filter(Boolean)}
                  onSelectPlayer={(userInfo, customName) => {
                    if (customName) {
                      const updated = {
                        ...localMatch,
                        teamA: {
                          ...localMatch.teamA,
                          player1: customName,
                          player1Id: "guest",
                        },
                      };
                      updateParent(updated);
                    } else if (userInfo) {
                      const updated = {
                        ...localMatch,
                        teamA: {
                          ...localMatch.teamA,
                          player1: userInfo.id || userInfo.user_id,
                          player1Id: userInfo.id || userInfo.user_id,
                        },
                      };
                      updateParent(updated);
                    }
                  }}
                />
              </div>
              <div>
                <label className={formClasses.label}>Player 2:</label>
                <PlayerSlot
                  availableUsers={availableUsers}
                  selectedPlayerId={localMatch.teamA?.player2Id}
                  selectedPlayerName={
                    localMatch.teamA?.player2Id === "guest"
                      ? localMatch.teamA?.player2 || "Player 2"
                      : localMatch.teamA?.player2
                      ? availableUsers.find(
                          (u) => (u.id || u.user_id) == localMatch.teamA.player2
                        )?.full_name || "Player 2"
                      : "Player 2"
                  }
                  selectedUserIds={[
                    localMatch.teamA?.player1Id !== "guest" &&
                    localMatch.teamA?.player1Id
                      ? localMatch.teamA.player1Id
                      : null,
                    localMatch.teamA?.player2Id !== "guest" &&
                    localMatch.teamA?.player2Id
                      ? localMatch.teamA.player2Id
                      : null,
                    localMatch.teamB?.player1Id !== "guest" &&
                    localMatch.teamB?.player1Id
                      ? localMatch.teamB.player1Id
                      : null,
                    localMatch.teamB?.player2Id !== "guest" &&
                    localMatch.teamB?.player2Id
                      ? localMatch.teamB.player2Id
                      : null,
                  ].filter(Boolean)}
                  onSelectPlayer={(userInfo, customName) => {
                    if (customName) {
                      const updated = {
                        ...localMatch,
                        teamA: {
                          ...localMatch.teamA,
                          player2: customName,
                          player2Id: "guest",
                        },
                      };
                      updateParent(updated);
                    } else if (userInfo) {
                      const updated = {
                        ...localMatch,
                        teamA: {
                          ...localMatch.teamA,
                          player2: userInfo.id || userInfo.user_id,
                          player2Id: userInfo.id || userInfo.user_id,
                        },
                      };
                      updateParent(updated);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Team B Selection */}
          <div className={formClasses.playerSection}>
            <h4 className="text-lg font-semibold text-gray-700 mb-3">Team B</h4>
            <div className={formClasses.playerRow}>
              <div>
                <label className={formClasses.label}>Player 1:</label>
                <PlayerSlot
                  availableUsers={availableUsers}
                  selectedPlayerId={localMatch.teamB?.player1Id}
                  selectedPlayerName={
                    localMatch.teamB?.player1Id === "guest"
                      ? localMatch.teamB?.player1 || "Player 1"
                      : localMatch.teamB?.player1
                      ? availableUsers.find(
                          (u) => (u.id || u.user_id) == localMatch.teamB.player1
                        )?.full_name || "Player 1"
                      : "Player 1"
                  }
                  selectedUserIds={[
                    localMatch.teamA?.player1Id !== "guest" &&
                    localMatch.teamA?.player1Id
                      ? localMatch.teamA.player1Id
                      : null,
                    localMatch.teamA?.player2Id !== "guest" &&
                    localMatch.teamA?.player2Id
                      ? localMatch.teamA.player2Id
                      : null,
                    localMatch.teamB?.player1Id !== "guest" &&
                    localMatch.teamB?.player1Id
                      ? localMatch.teamB.player1Id
                      : null,
                    localMatch.teamB?.player2Id !== "guest" &&
                    localMatch.teamB?.player2Id
                      ? localMatch.teamB.player2Id
                      : null,
                  ].filter(Boolean)}
                  onSelectPlayer={(userInfo, customName) => {
                    if (customName) {
                      const updated = {
                        ...localMatch,
                        teamB: {
                          ...localMatch.teamB,
                          player1: customName,
                          player1Id: "guest",
                        },
                      };
                      updateParent(updated);
                    } else if (userInfo) {
                      const updated = {
                        ...localMatch,
                        teamB: {
                          ...localMatch.teamB,
                          player1: userInfo.id || userInfo.user_id,
                          player1Id: userInfo.id || userInfo.user_id,
                        },
                      };
                      updateParent(updated);
                    }
                  }}
                />
              </div>
              <div>
                <label className={formClasses.label}>Player 2:</label>
                <PlayerSlot
                  availableUsers={availableUsers}
                  selectedPlayerId={localMatch.teamB?.player2Id}
                  selectedPlayerName={
                    localMatch.teamB?.player2Id === "guest"
                      ? localMatch.teamB?.player2 || "Player 2"
                      : localMatch.teamB?.player2
                      ? availableUsers.find(
                          (u) => (u.id || u.user_id) == localMatch.teamB.player2
                        )?.full_name || "Player 2"
                      : "Player 2"
                  }
                  selectedUserIds={[
                    localMatch.teamA?.player1Id !== "guest" &&
                    localMatch.teamA?.player1Id
                      ? localMatch.teamA.player1Id
                      : null,
                    localMatch.teamA?.player2Id !== "guest" &&
                    localMatch.teamA?.player2Id
                      ? localMatch.teamA.player2Id
                      : null,
                    localMatch.teamB?.player1Id !== "guest" &&
                    localMatch.teamB?.player1Id
                      ? localMatch.teamB.player1Id
                      : null,
                    localMatch.teamB?.player2Id !== "guest" &&
                    localMatch.teamB?.player2Id
                      ? localMatch.teamB.player2Id
                      : null,
                  ].filter(Boolean)}
                  onSelectPlayer={(userInfo, customName) => {
                    if (customName) {
                      const updated = {
                        ...localMatch,
                        teamB: {
                          ...localMatch.teamB,
                          player2: customName,
                          player2Id: "guest",
                        },
                      };
                      updateParent(updated);
                    } else if (userInfo) {
                      const updated = {
                        ...localMatch,
                        teamB: {
                          ...localMatch.teamB,
                          player2: userInfo.id || userInfo.user_id,
                          player2Id: userInfo.id || userInfo.user_id,
                        },
                      };
                      updateParent(updated);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <button onClick={handleAddGame} className={formClasses.addButton}>
          Add Game
        </button>
      </div>

      {localMatch.games.length > 0 && (
        <div className={formClasses.tableWrapper}>
          <table className={formClasses.table}>
            <thead>
              <tr>
                <th className={formClasses.tableHeader}>Game</th>
                <th className={formClasses.tableHeader}>User Points</th>
                <th className={formClasses.tableHeader}>Opponent Points</th>
                <th className={formClasses.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {localMatch.games.map((game, i) => {
                const isDeuce =
                  game.userPoints >= 10 && game.opponentPoints >= 10;
                const maxPoints = isDeuce ? 20 : 11;

                return (
                  <tr key={i}>
                    <td
                      className={`${formClasses.tableCell} font-semibold bg-gray-50`}
                    >
                      {i + 1}
                      {isDeuce && (
                        <div className="text-xs text-red-500 mt-1">DEUCE</div>
                      )}
                    </td>
                    <td className={formClasses.tableCell}>
                      <input
                        type="number"
                        value={game.userPoints}
                        onChange={(e) =>
                          handleGameChange(i, "userPoints", e.target.value)
                        }
                        min="0"
                        max={maxPoints}
                        className={formClasses.numberInput}
                      />
                      {isDeuce && (
                        <div className="text-xs text-orange-500 mt-1">
                          Win by 2
                        </div>
                      )}
                    </td>
                    <td className={formClasses.tableCell}>
                      <input
                        type="number"
                        value={game.opponentPoints}
                        onChange={(e) =>
                          handleGameChange(i, "opponentPoints", e.target.value)
                        }
                        min="0"
                        max={maxPoints}
                        className={formClasses.numberInput}
                      />
                      {isDeuce && (
                        <div className="text-xs text-orange-500 mt-1">
                          Win by 2
                        </div>
                      )}
                    </td>
                    <td className={formClasses.tableCell}>
                      <button
                        onClick={() => handleRemoveGame(i)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className={formClasses.scoreCard}>
        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.gameMode === "singles"
              ? localMatch.playerA
                ? availableUsers.find((u) => u.id == localMatch.playerA)
                    ?.full_name || "Player A"
                : "Player A"
              : "Team A"}{" "}
            Games Won
          </div>
          <div className={formClasses.scoreValue}>{localMatch.userGames}</div>
        </div>

        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.gameMode === "singles"
              ? localMatch.playerB
                ? availableUsers.find((u) => u.id == localMatch.playerB)
                    ?.full_name || "Player B"
                : "Player B"
              : "Team B"}{" "}
            Games Won
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.opponentGames}
          </div>
        </div>

        <div className={formClasses.winnerBox}>
          <div className="text-sm text-blue-600 mb-1">Match Type</div>
          <div className="text-2xl font-bold text-blue-800">
            {localMatch.matchType === "best_of_5"
              ? "Best of 5 (3 wins)"
              : "Best of 7 (4 wins)"}
          </div>
        </div>
      </div>
    </div>
  );
};

// PlayerSlot component for single player selection per slot
const PlayerSlot = ({
  availableUsers,
  selectedPlayerId,
  selectedPlayerName,
  onSelectPlayer,
  selectedUserIds = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isSlotEmpty =
    !selectedPlayerId ||
    !selectedPlayerName ||
    selectedPlayerName === "Player A" ||
    selectedPlayerName === "Player B" ||
    selectedPlayerName === "Player 1" ||
    selectedPlayerName === "Player 2";

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSave = () => {
    if (useCustomName && customName.trim()) {
      onSelectPlayer(null, customName.trim());
      setCustomName("");
      setIsEditing(false);
    } else if (!useCustomName && selectedUser) {
      onSelectPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setCustomName("");
    setUseCustomName(false);
    setIsEditing(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Show editing UI if editing or no player selected
  if (isEditing || isSlotEmpty) {
    return (
      <div className="flex flex-col gap-2">
        {!useCustomName ? (
          <>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Search for system users..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-48 overflow-y-auto z-50 shadow-lg">
                  {suggestions.map((user) => {
                    const isAlreadySelected = selectedUserIds.includes(
                      user.id || user.user_id
                    );
                    return (
                      <div
                        key={user.id || user.user_id}
                        className={`p-3 cursor-pointer border-b border-gray-100 transition-colors ${
                          isAlreadySelected
                            ? "opacity-50 bg-gray-100 cursor-not-allowed"
                            : "hover:bg-gray-50"
                        }`}
                        onClick={() =>
                          !isAlreadySelected && handleSelectUser(user)
                        }
                      >
                        <div className="font-medium text-gray-900">
                          {user.full_name || user.username}
                          {isAlreadySelected && (
                            <span className="text-red-500 ml-2">
                              (Already Added)
                            </span>
                          )}
                        </div>
                        {user.email && (
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {showSuggestions &&
                searchTerm.length >= 2 &&
                suggestions.length === 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg shadow-lg">
                    <div className="p-3 text-sm text-gray-500 text-center">
                      No users found
                    </div>
                  </div>
                )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!selectedUser}
                className={`bg-blue-500 text-white px-4 py-2 rounded-lg text-sm transition-all ${
                  selectedUser
                    ? "hover:bg-blue-600"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Enter custom player name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!customName.trim()}
                className={`bg-blue-500 text-white px-4 py-2 rounded-lg text-sm transition-all ${
                  customName.trim()
                    ? "hover:bg-blue-600"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        )}
        <span
          onClick={() => setUseCustomName(!useCustomName)}
          className="text-xs text-blue-500 cursor-pointer hover:underline"
        >
          {useCustomName ? "Use system users" : "Add custom name"}
        </span>
      </div>
    );
  }

  // If player is selected and not editing, show the selected player
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <span className="text-sm text-gray-800 flex-1">{selectedPlayerName}</span>
      <button
        onClick={() => setIsEditing(true)}
        className="bg-orange-400 hover:bg-orange-500 text-white px-3 py-1 rounded text-xs transition-colors"
      >
        Edit
      </button>
    </div>
  );
};

export default AddTableTennis;
